import axios from 'axios';
import { API_BASE_URL, ENDPOINTS } from '../config/api.config';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor for API calls
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for API calls
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle token expiration and authentication errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Clear token and user type from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      
      // Get the current path to determine where to redirect
      const path = window.location.pathname;
      const isFeederPath = path.includes('feeder');
      
      // Redirect to the appropriate login page
      if (isFeederPath) {
        window.location.href = '/feeder-login';
      } else {
        window.location.href = '/login';
      }
      
      return Promise.reject(error);
    }
    
    // Handle server errors
    if (error.response?.status === 500) {
      console.error('Server error:', error);
      // You could show a global notification here
    }
    
    return Promise.reject(error);
  }
);

/**
 * Authentication API methods
 */
export const authApi = {
  // User authentication
  loginUser: async (email, password) => {
    const response = await api.post(ENDPOINTS.AUTH.LOGIN_USER, { email, password });
    return response.data;
  },
  
  registerUser: async (name, email, password) => {
    const response = await api.post(ENDPOINTS.AUTH.REGISTER_USER, { name, email, password });
    return response.data;
  },
  
  // Feeder authentication
  loginFeeder: async (email, password) => {
    const response = await api.post(ENDPOINTS.AUTH.LOGIN_FEEDER, { email, password });
    return response.data;
  },
  
  registerFeeder: async (userData) => {
    const response = await api.post(ENDPOINTS.AUTH.REGISTER_FEEDER, userData);
    return response.data;
  },
  
  // Common authentication methods
  googleLogin: async (token) => {
    const response = await api.post(ENDPOINTS.AUTH.GOOGLE, { token });
    return response.data;
  },
  
  getUserProfile: async () => {
    const response = await api.get(ENDPOINTS.AUTH.PROFILE);
    return response.data;
  },
  
  getFeederProfile: async () => {
    const response = await api.get(ENDPOINTS.AUTH.FEEDER_PROFILE);
    return response.data;
  }
};

/**
 * Cats API methods
 */
export const catsApi = {
  getAllCats: async () => {
    const response = await api.get(ENDPOINTS.CATS.LIST);
    return response.data;
  },
  
  getCatById: async (id) => {
    const response = await api.get(ENDPOINTS.CATS.DETAILS(id));
    return response.data;
  },
  
  getCatsCount: async () => {
    try {
      console.log('Fetching cats count from API...');
      const response = await api.get(ENDPOINTS.CATS.COUNT);
      console.log('Raw cats count response:', response);
      
      // Handle different response formats
      let count = 0;
      if (response.data !== undefined) {
        if (typeof response.data === 'number') {
          count = response.data;
        } else if (response.data.count !== undefined) {
          count = response.data.count;
        } else if (response.data.totalCount !== undefined) {
          count = response.data.totalCount;
        } else {
          // If it's just an array, return its length
          count = Array.isArray(response.data) ? response.data.length : 0;
        }
      }
      
      console.log('Processed cats count:', count);
      return { count };
    } catch (error) {
      console.error('Error in getCatsCount:', error);
      return { count: 0 };
    }
  },
  
  // Get cats belonging to the feeder
  getFeederCats: async () => {
    try {
      const response = await api.get('/feeder/cats');
      console.log('Feeder cats API response:', response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Add a new cat (for feeders)
  addCat: async (catData) => {
    try {
      const response = await api.post('/cat', catData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Update an existing cat (for feeders)
  updateCat: async (catId, catData) => {
    try {
      const response = await api.put(`/cat/${catId}`, catData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Safely delete a cat (for feeders)
  deleteCat: async (catId) => {
    try {
      const response = await api.delete(`/cat/safe-delete/${catId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

/**
 * Feedings API methods
 */
export const feedingsApi = {
  createFeeding: async (feedingData, catId) => {
    try {
      // Use the provided catId parameter to construct the endpoint
      const response = await api.post(ENDPOINTS.FEEDING.CREATE(catId), feedingData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getCatFeedings: async (catId, page, size) => {
    const queryParams = {};
    if (page !== undefined) queryParams.page = page;
    if (size !== undefined) queryParams.size = size;
    
    const response = await api.get(
      ENDPOINTS.FEEDING.LIST_FOR_CAT(catId),
      { params: queryParams }
    );
    return response.data;
  },
  
  getLastFeeding: async (catId) => {
    const response = await api.get(ENDPOINTS.FEEDING.LAST_FOR_CAT(catId));
    return response.data;
  },
  
  getUserFeedingHistory: async (page, size) => {
    const queryParams = {};
    if (page !== undefined) queryParams.page = page;
    if (size !== undefined) queryParams.size = size;
    
    // This endpoint would need to be implemented on the backend
    // It would return feedings related to the user's requests
    const response = await api.get('/user/feedings/history', { params: queryParams });
    return response.data;
  },
  
  // Get feedings recorded by the feeder
  getFeederFeedings: async (page, size) => {
    try {
      const queryParams = {};
      
      // Make sure we're starting with page 0 for backend compatibility
      if (page !== undefined) {
        queryParams.page = page;
        console.log(`Setting page parameter to: ${page}`);
      }
      
      if (size !== undefined) {
        queryParams.size = size;
        console.log(`Setting size parameter to: ${size}`);
      }
      
      console.log('Fetching feeder feedings with params:', queryParams);
      
      const response = await api.get('/feeder/feedings', { 
        params: queryParams 
      });
      
      console.log('Feeder feedings response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getFeederFeedings:', error);
      throw error;
    }
  },
  
  // Get a specific feeding by ID
  getFeedingById: async (feedingId) => {
    try {
      const response = await api.get(`/feedings/${feedingId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

/**
 * Feeding Requests API methods
 */
export const feedingRequestsApi = {
  createFeedingRequest: async (catId) => {
    const response = await api.post(ENDPOINTS.FEEDING_REQUEST.CREATE(catId));
    return response.data;
  },
  
  getUserFeedingRequests: async (page, size) => {
    const queryParams = {};
    if (page !== undefined) queryParams.page = page;
    if (size !== undefined) queryParams.size = size;
    
    const response = await api.get(
      ENDPOINTS.FEEDING_REQUEST.LIST,
      { params: queryParams }
    );
    return response.data;
  },
  
  getOpenFeedingRequests: async (page, size) => {
    try {
      const queryParams = {};
      if (page !== undefined) queryParams.page = page;
      if (size !== undefined) queryParams.size = size;
      
      console.log('Fetching open feeding requests with params:', queryParams);
      const response = await api.get(
        ENDPOINTS.FEEDING_REQUEST.OPEN,
        { params: queryParams }
      );
      
      console.log('Open feeding requests API response:', response);
      
      // Handle different response formats
      if (response.data) {
        // If it's an object with a data property, return that
        if (Array.isArray(response.data)) {
          return response.data;
        }
        return response.data;
      } else {
        // If the response is directly the data
        return response;
      }
    } catch (error) {
      console.error('Error fetching open feeding requests:', error);
      throw error;
    }
  },
  
  getProcessedFeedingRequests: async (page, size) => {
    try {
      const queryParams = {};
      if (page !== undefined) queryParams.page = page;
      if (size !== undefined) queryParams.size = size;
      
      console.log('Fetching processed feeding requests with params:', queryParams);
      const response = await api.get(
        ENDPOINTS.FEEDING_REQUEST.PROCESSED,
        { params: queryParams }
      );
      
      console.log('Processed feeding requests API response:', response);
      
      // Handle different response formats
      if (response.data) {
        // If it's an object with a data property, return that
        if (Array.isArray(response.data)) {
          return response.data;
        }
        return response.data;
      } else {
        // If the response is directly the data
        return response;
      }
    } catch (error) {
      console.error('Error fetching processed feeding requests:', error);
      throw error;
    }
  },
  
  getFeedingStats: async () => {
    const response = await api.get(ENDPOINTS.FEEDING_REQUEST.STATS);
    return response.data;
  },
};

/**
 * Notifications API methods
 */
export const notificationsApi = {
  getVapidPublicKey: async () => {
    const response = await api.get(ENDPOINTS.NOTIFICATIONS.VAPID_KEY);
    return response.data;
  },
  
  saveSubscription: async (subscription) => {
    const response = await api.post(ENDPOINTS.NOTIFICATIONS.SUBSCRIBE, subscription);
    return response.data;
  },
  
  isSubscribed: async (endpoint) => {
    const response = await api.get(`${ENDPOINTS.NOTIFICATIONS.IS_SUBSCRIBED}?endpoint=${encodeURIComponent(endpoint)}`);
    return response.data;
  },
  
  deleteSubscription: async (endpoint) => {
    const response = await api.delete(ENDPOINTS.NOTIFICATIONS.UNSUBSCRIBE, {
      data: { endpoint },
    });
    return response.data;
  },
};

// User API
export const userApi = {
  // Get user profile (for regular users)
  getUserProfile: async () => {
    try {
      const response = await api.get('/user/profile');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Update user profile (for regular users)
  updateUserProfile: async (profileData) => {
    try {
      const response = await api.put('/user/profile', profileData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Get feeder profile
  getFeederProfile: async () => {
    try {
      const response = await api.get('/feeder/profile');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Update feeder profile
  updateFeederProfile: async (profileData) => {
    try {
      const response = await api.put('/feeder/profile', profileData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Get feeder statistics (totalCats, completedRequests, totalFeedings, pendingRequests)
  getFeederStats: async () => {
    try {
      const response = await api.get('/feeder/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Change password (works for both users and feeders)
  changePassword: async (passwordData) => {
    try {
      const response = await api.post('/auth/change-password', passwordData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Default export for the axios instance
export default api; 